/**
 * PlaceReviewsView — presentational Google-reviews branch of PlaceDetailModule
 * (currentView === 'reviews'). The reviews query lives in the parent; its
 * data/loading/error states are threaded down as props.
 */

interface PlaceReview {
  author: string;
  authorPhotoUri: string | null;
  rating: number;
  text: string;
  relativeTime: string;
  publishTime: string | null;
}

interface ReviewsData {
  placeName: string;
  rating: number | null;
  totalReviews: number;
  reviews: PlaceReview[];
}

interface PlaceReviewsViewProps {
  reviewsData: ReviewsData | undefined;
  reviewsLoading: boolean;
  reviewsError: Error | null;
  placeTitle: string;
  onBack: () => void;
  isMobile: boolean;
}

export function PlaceReviewsView({
  reviewsData,
  reviewsLoading,
  reviewsError,
  placeTitle,
  onBack,
  isMobile,
}: PlaceReviewsViewProps) {
  const renderStars = (rating: number) => {
    return (
      <div style={{ display: "flex", gap: "2px" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill={star <= rating ? "#fbbf24" : "var(--color-border-strong)"}
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        height: isMobile ? "100dvh" : "75vh", // Full height on mobile, 75vh on desktop
        width: isMobile ? "100vw" : "100%", // Full width on mobile
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: isMobile ? "fixed" : "relative",
        top: isMobile ? 0 : "auto",
        left: isMobile ? 0 : "auto",
        right: isMobile ? 0 : "auto",
        bottom: isMobile ? 0 : "auto",
        background: "var(--color-surface)",
        borderRadius: isMobile ? "0" : "12px",
        zIndex: isMobile ? 10000 : "auto",
      }}
    >
      {/* Header with back button */}
      <div
        style={{
          position: "absolute",
          top: "16px",
          left: "16px",
          right: "16px",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "var(--color-surface)",
            border: "none",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
          title="Back"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
        </button>
      </div>

      {/* Content area */}
      <div
        className="xv-scroll"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "60px 20px 20px",
          minHeight: 0,
        }}
      >
        <h2
          style={{
            fontSize: "24px",
            fontWeight: "600",
            marginBottom: "12px",
          }}
        >
          {reviewsData?.placeName || placeTitle}
        </h2>

        {reviewsData && reviewsData.rating && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "20px",
            }}
          >
            {renderStars(Math.round(reviewsData.rating))}
            <span
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: "var(--color-text-subtle)",
              }}
            >
              {reviewsData.rating.toFixed(1)}
            </span>
            <span style={{ fontSize: "14px", color: "var(--color-text-muted)" }}>
              ({reviewsData.totalReviews.toLocaleString()} reviews)
            </span>
          </div>
        )}

        {reviewsLoading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 20px",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  border: "3px solid var(--color-border)",
                  borderTop: "3px solid var(--color-primary)",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 16px",
                }}
              ></div>
              <p style={{ color: "var(--color-text-muted)" }}>
                Loading reviews...
              </p>
            </div>
          </div>
        )}

        {reviewsError && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-danger)"
              strokeWidth="2"
              style={{ margin: "0 auto 16px" }}
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p style={{ fontWeight: "500", color: "var(--color-danger)" }}>
              Failed to load reviews
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "var(--color-text-muted)",
                marginTop: "4px",
              }}
            >
              {reviewsError instanceof Error
                ? reviewsError.message
                : "Failed to load reviews"}
            </p>
          </div>
        )}

        {reviewsData && !reviewsLoading && !reviewsError && (
          <div>
            {reviewsData.reviews.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "var(--color-text-muted)",
                  padding: "32px 0",
                }}
              >
                No reviews available for this place
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {reviewsData.reviews.map((review, idx) => (
                  <div
                    key={idx}
                    style={{
                      borderBottom:
                        idx < reviewsData.reviews.length - 1
                          ? "1px solid var(--color-border)"
                          : "none",
                      paddingBottom: "16px",
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <div
                        style={{
                          fontWeight: "500",
                          color: "var(--color-text)",
                        }}
                      >
                        {review.author}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginTop: "2px",
                        }}
                      >
                        {renderStars(review.rating)}
                        <span
                          style={{
                            fontSize: "12px",
                            color: "var(--color-text-muted)",
                          }}
                        >
                          {review.relativeTime}
                        </span>
                      </div>
                    </div>

                    {review.text && (
                      <p
                        style={{
                          color: "var(--color-text-subtle)",
                          fontSize: "14px",
                          lineHeight: "1.6",
                        }}
                      >
                        {review.text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {reviewsData && (
        <div
          style={{
            padding: "16px 20px",
            borderTop: "1px solid var(--color-border)",
            background: "var(--color-surface-muted)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <p style={{ fontSize: "14px", color: "var(--color-text-muted)" }}>
            Showing {reviewsData.reviews.length} most recent reviews
          </p>
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent(placeTitle + " Reviews")}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "8px 16px",
              background: "var(--color-primary)",
              color: "white",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "500",
              textDecoration: "none",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--color-primary-hover)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "var(--color-primary)")
            }
          >
            View All on Google
          </a>
        </div>
      )}
    </div>
  );
}
